import { formatMoney } from '@duebook/ledger-core';

/**
 * Display formatting only. The backend has already done every calculation; this file turns strings
 * of minor units into something a merchant reads. No arithmetic on money happens here beyond the
 * exact integer formatting `ledger-core` provides (CLAUDE.md §6).
 */

export function money(minorUnits: string, currency: string): string {
  const formatted = formatMoney(BigInt(minorUnits));
  return `${formatted} ${currency}`;
}

/** Compact form for dense table cells: a dash instead of a row of zeroes. */
export function moneyOrDash(minorUnits: string): string {
  if (BigInt(minorUnits) === 0n) return '—';
  return formatMoney(BigInt(minorUnits));
}

/**
 * Minor units to an editable major-unit string: "400000" -> "4000.00".
 *
 * The credit-limit field is read AND written in major units — `setCreditLimit` posts what the
 * merchant typed and the API answers in minor units. Seeding that field straight from `limitCents`
 * (as it did until 25 Aug 2026) showed a $4,000 limit as "400000" and, on save, would have stored
 * four hundred thousand dollars: a 100x error on the one number that gates checkout.
 *
 * `formatMoney` emits no thousands separators, so the result round-trips back through the API.
 */
export function toMajorUnits(minorUnits: string): string {
  return formatMoney(BigInt(minorUnits));
}

export function isNegative(minorUnits: string): boolean {
  return BigInt(minorUnits) < 0n;
}

/** True when the company's exposure has reached or passed its credit limit. */
export function isOverLimit(openBalanceCents: string, limitCents: string | null): boolean {
  if (limitCents === null) return false;
  return BigInt(openBalanceCents) >= BigInt(limitCents);
}
